import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../Caches.module.css";
import LruEngine from "../mapengine/LruEngine";

const LruWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [capacity, setCapacity] = useState(4);
  const [keyInput, setKeyInput] = useState("");
  const [valInput, setValInput] = useState("");
  const [getKey, setGetKey] = useState("");
  const [lastResult, setLastResult] = useState<string | null>(null);
  const [activeLine, setActiveLine] = useState<number | null>(null);
  const {
    capacity: engineCapacity,
    setCapacity: setEngineCapacity,
    map,
    list,
    get,
    put,
    reset,
    highlightKey,
    highlightNodeId,
    animating,
  } = LruEngine(capacity);

  const handleCapacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = Number(e.target.value);
    const newCap = isNaN(raw) || raw < 1 ? 1 : raw;
    setCapacity(newCap);
    setEngineCapacity(newCap);
    reset();
    setLastResult(null);
  };

  const handleReset = () => {
    reset();
    setKeyInput("");
    setValInput("");
    setGetKey("");
    setActiveLine(null);
    setLastResult(null);
  };

  const handlePut = async () => {
    if (!keyInput || !valInput) return;
    setActiveLine(1); 
    await put(keyInput, valInput);
    setLastResult(null);
  };

  const handleGet = async () => {
    if (!getKey) return;
    const value = await get(getKey);
    if (value === null) {
      setLastResult(`Miss: key "${getKey}" not found`);
    } else {
      setLastResult(`Hit: ${value}`);
    }
  };

  const handleGenerateRandom = async () => {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    reset();
    setLastResult(null);
    setActiveLine(null);

    const count = Math.min(capacity, alphabet.length);
    for (let i = 0; i < count; i++) {
      const key = alphabet[i];
      const value = String(Math.floor(Math.random() * 90) + 10);
      await put(key, value);
    }
  };
  const mapNodes = Array.from(map.values());

  return (
    <div className={styles.cachePageContainer}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>

      <h1 className={styles.title}>LRU Cache (Least Recently Used)</h1>

      <p className={styles.description}>
        The <strong> LRU Cache </strong> keeps track of the order in which items are used. When the cache is full, the least recently used item is removed first. This design combines a hash map (for O(1) lookup) with a doubly linked list (to track usage order).<br></br>
       <strong>Hash Map:</strong> stored as key → node and visualized as an array of objects.<br></br>
        <strong>Linked List:</strong> shows usage order (head = most recently used).
      </p>

      <div className={styles.useCase}>
        <h3>Use Cases</h3>
       <strong>LRU caches</strong>  are widely used in web browsers, memory caches, database page replacement policies, and OS caching layers.
      </div>

      <div className={styles.timeComplexity}>
        <h3>Time Complexity</h3>
        Both get and put operations run in O(1) average time by combining hash map lookup with linked-list updates.      </div>

      <div className={styles.controlsBar}>
        <input
          type="number"
          value={capacity}
          min={1}
          onChange={handleCapacityChange}
          className={styles.controlInput}
        />
        <button className={styles.cacheButton}>Capacity</button>

        <input
          placeholder="key"
          value={keyInput}
          onChange={(e) => setKeyInput(e.target.value)}
          className={styles.controlInput}
        />
        <input
          placeholder="value"
          value={valInput}
          onChange={(e) => setValInput(e.target.value)}
          className={styles.controlInput}
        />

        <button className={styles.cacheButton} onClick={handlePut}>
          Put
        </button>

        <input
          placeholder="key"
          value={getKey}
          onChange={(e) => setGetKey(e.target.value)}
          className={styles.controlInput}
        />
        <button className={styles.cacheButton} onClick={handleGet}>
          Get
        </button>

        <button className={styles.cacheButton} onClick={handleReset}>
          Reset
        </button>

        <button
          className={`${styles.cacheButton} ${styles.randomButton}`}
          onClick={handleGenerateRandom}
        >
          Random
        </button>
      </div>

      {lastResult && (
        <p style={{ marginTop: "0.4rem", textAlign: "center" }}>{lastResult}</p>
      )}

      <div className={styles.sectionCard}>
        <h3>Hash Map (key → node)</h3>

        <div className={styles.arrayDisplay}>
          <span className={styles.arrayBracket}>[</span>
          <div className={styles.arrayItems}>
            {mapNodes.map((node, index) => (
              <div
                key={node.id}
                className={`${styles.arrayItem} ${
                  highlightNodeId === node.id ? styles.arrayItemActive : ""
                }`}
              >
                {`{ ${node.key}: ${node.value} }`}
                {index < mapNodes.length - 1 && <span>,</span>}
              </div>
            ))}
          </div>
          <span className={styles.arrayBracket}>]</span>
        </div>
      </div>

      <div className={styles.sectionCard}>
        <h3>Recently Bucket (LRU)</h3>

        <div className={styles.dllContainer}>
          <div className={styles.dllEnd}>HEAD</div>
          <div className={styles.dllArrow}>⇄</div>

          {list.length === 0 && (
            <div className={styles.emptyMsg}>List empty.</div>
          )}

          {list.map((node, index) => (
            <React.Fragment key={node.id}>
              <div
                className={`${styles.dllNode} ${
                  highlightNodeId === node.id ? styles.highlightNode : ""
                }`}
              >
                {node.key}:{node.value}
              </div>
              {index < list.length - 1 && (
                <div className={styles.dllArrow}>⇄</div>
              )}
            </React.Fragment>
          ))}

          {list.length > 0 && (
            <>
              <div className={styles.dllArrow}>⇄</div>
              <div className={styles.dllEnd}>TAIL</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LruWrapper;
